import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { clientHandoff, hasSession, serverAuth } from "@/data/auth";
import { nextFor } from "@/app/safe-next";
import { AppShell } from "@/app/AppShell";
import { TourHost } from "@/tours/TourHost";
import { FirstPasswordGate } from "@/features/signin/FirstPasswordGate";

// X15-0102c: the client hand-off is asked once per page load, so a client never draws the team app before it is moved
let handoffAsked = false;

/** Every team page sits inside the app shell. The portal and public pages use their own layouts. */
export const Route = createFileRoute("/_app")({
  // server mode: a visitor with no session goes to sign in instead of an app full of failed reads. A failed sign-in link
  // lands here with ?error=EXPIRED_TOKEN or INVALID_TOKEN, which the sign-in page turns into its banner.
  beforeLoad: async ({ location }) => {
    if (!serverAuth || (await hasSession()) !== false) {
      if (serverAuth && !handoffAsked) {
        handoffAsked = true;
        const url = await clientHandoff();
        // a client goes to the client dashboard signed in there; this promise never resolves, so the team app never draws
        if (url) {
          window.location.assign(url);
          return new Promise<never>(() => {});
        }
      }
      return;
    }
    const error = (location.search as { error?: unknown }).error;
    // the page asked for comes back as the return path, so the person lands on it once signed in
    const next = nextFor(location.href, "/home");
    throw redirect({ to: "/sign-in", search: { ...(typeof error === "string" ? { error } : {}), ...(next ? { next } : {}) } });
  },
  component: () => (
    <TourHost>
      <AppShell>
        <FirstPasswordGate>
          <Outlet />
        </FirstPasswordGate>
      </AppShell>
    </TourHost>
  ),
});
